import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { createStripeCheckoutSession, getStripeConfig } from '../../services/api';
import { Container, Card, Button, Alert, Spinner, Badge } from '../common/LegacyUi';
import { useAuth } from '../auth/AuthContext';
import { clearStoredPartnerCode, getStoredPartnerCode, storePartnerCode } from '../../utils/partnerTracking';
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Check,
  CircleDollarSign,
  Clock3,
  Globe2,
  Headphones,
  LayoutDashboard,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import './PaymentPlans.scss';

const getApiErrorMessage = (err, fallback) => (
  err.response?.data?.error
  || err.response?.data?.detail
  || err.message
  || fallback
);

const BILLING_PERIODS = {
  monthly: {
    labelKey: 'payment.billing.monthly',
    periodKey: 'payment.billing.monthly_period',
    months: 1,
  },
  semiannual: {
    labelKey: 'payment.billing.semiannual',
    periodKey: 'payment.billing.semiannual_period',
    months: 6,
  },
  annual: {
    labelKey: 'payment.billing.annual',
    periodKey: 'payment.billing.annual_period',
    months: 12,
  },
};

const PaymentPlans = ({ offerId = null, returnUrl = '/business/offers' }) => {
  const { t } = useTranslation('business');
  const { isAuthenticated, subscriptionInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Obtener offerId de los query params si no se pasa como prop
  const urlParams = new URLSearchParams(location.search);
  const offerIdFromUrl = urlParams.get('offerId');
  const partnerCodeFromUrl = urlParams.get('partner');
  const finalOfferId = offerId || offerIdFromUrl;
  const activePartnerCode = partnerCodeFromUrl || getStoredPartnerCode();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [subscriptionConflict, setSubscriptionConflict] = useState(false);

  const hasActiveSubscription = Boolean(subscriptionInfo?.has_active_subscription);

  // Obtener planes de pago disponibles
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await api.get('/plans/');
        const configResponse = await getStripeConfig();

        console.log('Plans response:', response.data);

        // Asegurar que la respuesta es un array
        const allPlans = Array.isArray(response.data) ? response.data :
          (response.data.results ? response.data.results : []);
        const plansData = allPlans.filter(plan => !plan?.plan_type || plan.plan_type === 'subscription');

        setPlans(plansData);
        setStripeConfig(configResponse.data);

        const recommendedPlan = plansData.find(plan => plan?.is_highlighted || plan?.is_popular) || plansData[0];
        setSelectedPlan(recommendedPlan?.id || null);
      } catch (err) {
        console.error('Error al cargar planes:', err);
        setError('No se pudieron cargar los planes de pago. Por favor, inténtalo de nuevo más tarde.');
        setPlans([]); // Asegurar que siempre sea un array
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPlans();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (partnerCodeFromUrl) {
      storePartnerCode(partnerCodeFromUrl);
    }
  }, [partnerCodeFromUrl]);

  const handleStripeCheckout = async () => {
    if (!selectedPlan || processingPayment) return;

    if (hasActiveSubscription) {
      setSubscriptionConflict(true);
      setError('Ya tienes una suscripción activa. Adminístrala desde la sección de suscripciones.');
      return;
    }

    try {
      setProcessingPayment(true);
      setError('');
      setSubscriptionConflict(false);
      const response = await createStripeCheckoutSession({
        planId: selectedPlan,
        offerId: finalOfferId,
        affiliateCode: getStoredPartnerCode(),
      });

      if (response.data?.checkout_url) {
        clearStoredPartnerCode();
        window.location.href = response.data.checkout_url;
        return;
      }

      setError('Stripe todavia no devolvio una URL de checkout.');
    } catch (err) {
      console.error('Error creating Stripe checkout session:', err);
      if (err.response?.status === 409 && err.response?.data?.code === 'active_subscription_exists') {
        setSubscriptionConflict(true);
      }
      setError(getApiErrorMessage(err, 'Stripe Checkout todavia no esta disponible.'));
    } finally {
      setProcessingPayment(false);
    }
  };
  // Filtrar planes según el período de facturación seleccionado
  const filteredPlans = Array.isArray(plans) ? plans.filter(plan => plan && plan.billing_period === billingPeriod) : [];
  const selectedPlanDetails = filteredPlans.find(plan => plan?.id === selectedPlan) || null;
  const selectedPlanIsUnlimited = selectedPlanDetails?.max_collabs === 0;
  const billingMeta = Object.fromEntries(
    Object.entries(BILLING_PERIODS).map(([period, meta]) => [
      period,
      {
        ...meta,
        label: t(meta.labelKey),
        period: t(meta.periodKey),
      },
    ])
  );
  const selectedBillingLabel = billingMeta[selectedPlanDetails?.billing_period]?.label || t('payment.billing.monthly');

  // Agrupar planes por número de collabs para mostrar comparaciones
  const groupedPlans = filteredPlans.reduce((acc, plan) => {
    if (!plan || typeof plan.max_collabs === 'undefined') {
      console.warn('Plan con datos incompletos:', plan);
      return acc;
    }

    const key = plan.max_collabs === 0 ? 'unlimited' : plan.max_collabs;
    if (!acc[key]) acc[key] = [];
    acc[key].push(plan);
    return acc;
  }, {});

  // Calcular descuento contra pagar mes a mes durante el mismo periodo
  const calculatePeriodDiscount = (collabCount, period) => {
    if (!Array.isArray(plans)) return null;
    const selectedMeta = billingMeta[period];
    if (!selectedMeta || period === 'monthly') return null;

    const monthlyPlan = plans.find(p => p && p.max_collabs === collabCount && p.billing_period === 'monthly');
    const periodPlan = plans.find(p => p && p.max_collabs === collabCount && p.billing_period === period);

    if (monthlyPlan && periodPlan && monthlyPlan.price && periodPlan.price) {
      const monthlyTotal = Number(monthlyPlan.price) * selectedMeta.months;
      const savings = monthlyTotal - Number(periodPlan.price);
      const percentage = Math.round((savings / monthlyTotal) * 100);
      return { savings: savings.toFixed(2), percentage };
    }
    return null;
  };

  const formatCurrency = (value) => (
    `$${Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  );

  const selectBillingPeriod = (nextPeriod) => {
    const currentPlan = plans.find(plan => plan?.id === selectedPlan);
    const matchingPlan = plans.find(plan => (
      plan?.billing_period === nextPeriod
      && plan?.max_collabs === currentPlan?.max_collabs
    ));

    setBillingPeriod(nextPeriod);
    setSelectedPlan(matchingPlan?.id || null);
  };

  if (loading) {
    return (
      <Container className="payment-plans-container payment-plans-loading">
        <div className="loading-orb"><Spinner /></div>
        <p>Cargando planes de pago...</p>
      </Container>
    );
  }

  return (
    <Container className="payment-plans-container">
      {error && (
        <Alert variant={subscriptionConflict ? 'warning' : 'danger'} className="payment-alert payment-alert--error">
          <div className="payment-alert__content">
            <ShieldCheck size={18} aria-hidden="true" />
            <span>{error}</span>
          </div>
          {subscriptionConflict && (
            <Button
              variant="dark"
              size="sm"
              className="payment-alert__action"
              onClick={() => navigate('/business/subscriptions')}
            >
              Administrar suscripción <ArrowUpRight size={14} aria-hidden="true" />
            </Button>
          )}
        </Alert>
      )}

      {hasActiveSubscription && !subscriptionConflict && (
        <Alert variant="warning" className="payment-alert payment-alert--subscription">
          <div className="payment-alert__content">
            <Clock3 size={18} aria-hidden="true" />
            <div>
              <strong>Tu cuenta tiene una suscripción activa</strong>
              <span>Puedes revisar o administrar tu suscripción desde el panel de suscripciones.</span>
            </div>
          </div>
          <Button variant="dark" size="sm" className="payment-alert__action" onClick={() => navigate('/business/subscriptions')}>
            Administrar suscripción <ArrowUpRight size={14} aria-hidden="true" />
          </Button>
        </Alert>
      )}

      {location.state?.message && (
        <Alert variant="success" className="payment-alert payment-alert--success" onClose={() => navigate(location.pathname, { state: {} })} dismissible>
          <div className="payment-alert__content"><Check size={18} aria-hidden="true" /><span>{location.state.message}</span></div>
        </Alert>
      )}

      <div className="payment-intro">
        <div className="payment-eyebrow"><Sparkles size={14} aria-hidden="true" /> Planes FOTH</div>
        <h1>{finalOfferId ? 'Elige tu plan de collabs.' : 'Elige el ritmo que tu marca necesita.'}</h1>
        <p>{finalOfferId
          ? 'Cada plan respeta sus límites de collabs y creators. Elige uno que cubra las necesidades de este collab.'
          : 'Más claridad para planear tus colaboraciones. Selecciona un plan y continúa de forma segura con Stripe.'}
        </p>
      </div>

      {activePartnerCode && (
        <Alert variant="light" className="partner-note">
          <Globe2 size={16} aria-hidden="true" />
          <span>Acceso recomendado por partner: <strong>{activePartnerCode}</strong></span>
        </Alert>
      )}

      <div className="payment-principles">
        <div className="payment-principle"><ShieldCheck size={17} aria-hidden="true" /><div><strong>Precios transparentes</strong><span>Sin cargos ocultos.</span></div></div>
        <div className="payment-principle"><Check size={17} aria-hidden="true" /><div><strong>Pagos protegidos</strong><span>Stripe procesa tus datos.</span></div></div>
        <div className="payment-principle"><Sparkles size={17} aria-hidden="true" /><div><strong>Escala con tu operación</strong><span>Sube de plan cuando quieras.</span></div></div>
      </div>

      <div className="billing-row">
        <span className="billing-label">Elige tu frecuencia de pago</span>
        <div className="billing-switch" role="group" aria-label="Frecuencia de pago">
          <button type="button" className={billingPeriod === 'monthly' ? 'is-active' : ''} aria-pressed={billingPeriod === 'monthly'} onClick={() => selectBillingPeriod('monthly')}>
            {t('payment.billing.monthly')}
          </button>
          <button type="button" className={billingPeriod === 'semiannual' ? 'is-active' : ''} aria-pressed={billingPeriod === 'semiannual'} onClick={() => selectBillingPeriod('semiannual')}>
            {t('payment.billing.semiannual')} <span className="saving-badge">{t('payment.savings.semiannual')}</span>
          </button>
          <button type="button" className={billingPeriod === 'annual' ? 'is-active' : ''} aria-pressed={billingPeriod === 'annual'} onClick={() => selectBillingPeriod('annual')}>
            {t('payment.billing.annual')} <span className="saving-badge">{t('payment.savings.annual')}</span>
          </button>
        </div>
      </div>

      <div className="plans-checkout-layout">
        <div className="plans-list">
          {Object.entries(groupedPlans).length === 0 ? (
            <Alert variant="info" className="empty-plans-alert">
              <h2>No hay planes disponibles</h2>
              <p>No se encontraron planes para el período de facturación seleccionado.</p>
            </Alert>
          ) : (
            <div className="plans-grid">
              {Object.entries(groupedPlans).map(([collabCount, planGroup]) => {
                const plan = planGroup[0];

                if (!plan || !plan.id || typeof plan.max_collabs === 'undefined' || typeof plan.max_creators_per_collab === 'undefined') {
                  console.warn('Saltando plan con datos incompletos:', plan);
                  return null;
                }

                const isUnlimited = collabCount === 'unlimited';
                const isRecommended = Boolean(plan.is_highlighted || plan.is_popular);
                const planKicker = isUnlimited ? 'Para escalar' : (isRecommended ? 'Para crecer' : 'Para empezar');
                const periodDiscount = calculatePeriodDiscount(plan.max_collabs, billingPeriod);
                const planBillingMeta = billingMeta[plan.billing_period] || billingMeta.monthly;
                const featureItems = [
                  { Icon: Check, label: isUnlimited ? 'Collabs de canje ilimitados' : `${plan.max_collabs} collab${plan.max_collabs > 1 ? 's' : ''} de canje activas` },
                  { Icon: CircleDollarSign, label: isUnlimited ? 'Collabs ilimitados' : `${plan.max_collabs} collabs activas` },
                  { Icon: Users, label: `${isUnlimited ? 'Creators ilimitados' : `Hasta ${plan.max_creators_per_collab} creators`} por collab` },
                  { Icon: LayoutDashboard, label: 'Dashboard de gestión' },
                  { Icon: BarChart3, label: 'Analíticas de rendimiento' },
                ];

                if (isUnlimited) featureItems.push({ Icon: Headphones, label: 'Soporte prioritario' });

                return (
                  <Card
                    key={plan.id}
                    className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${isRecommended ? 'recommended' : ''}`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {isRecommended && <div className="plan-ribbon">{plan.is_popular ? 'Más elegido' : 'Recomendado'}</div>}
                    <Card.Body>
                      <div className="plan-kicker">{planKicker}</div>
                      <Card.Title className="plan-title">{plan.name || 'Plan FOTH'}</Card.Title>
                      <Card.Text className="plan-description">{plan.description || 'Una forma más clara de organizar tus colaboraciones.'}</Card.Text>
                      <div className="plan-price">
                        <span className="currency">$</span>
                        <span className="amount">{Number(plan.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className="period">{planBillingMeta.period}</span>
                      </div>
                      <div className="plan-savings">
                        {periodDiscount
                          ? t('payment.savings.vs_monthly', { amount: periodDiscount.savings, percentage: periodDiscount.percentage })
                          : 'Sin compromiso de permanencia'}
                      </div>
                      <ul className="plan-features">
                        {featureItems.map(({ Icon, label }) => (
                          <li key={label}><Icon size={14} aria-hidden="true" /><span>{label}</span></li>
                        ))}
                      </ul>
                    </Card.Body>
                    <Card.Footer>
                      <Button
                        variant={selectedPlan === plan.id ? 'dark' : 'outline-dark'}
                        className="select-plan-btn"
                        aria-pressed={selectedPlan === plan.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedPlan(plan.id);
                        }}
                      >
                        {selectedPlan === plan.id ? 'Plan seleccionado' : 'Seleccionar plan'}
                      </Button>
                    </Card.Footer>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <aside className={`checkout-panel ${selectedPlanDetails ? 'is-ready' : ''}`} aria-live="polite">
          {!selectedPlanDetails ? (
            <div className="checkout-empty">
              <span className="checkout-kicker">Resumen de compra</span>
              <h2>Selecciona un plan</h2>
              <p>El resumen y el botón de Stripe aparecerán aquí.</p>
            </div>
          ) : (
            <>
              <div className="checkout-header">
                <div>
                  <span className="checkout-kicker">Resumen de compra</span>
                  <div className="checkout-plan-name">{selectedPlanDetails.name}</div>
                  <span className="checkout-subtitle">{selectedPlanDetails.description || 'Plan FOTH'}</span>
                </div>
                {stripeConfig?.test_mode && <Badge bg="warning" className="stripe-mode-badge">Modo prueba</Badge>}
              </div>

              <div className="checkout-price">
                <span>{formatCurrency(selectedPlanDetails.price)}</span>
                <small>{billingMeta[selectedPlanDetails.billing_period]?.period || t('payment.billing.monthly_period')}</small>
              </div>

              <dl className="checkout-summary">
                <div><dt>Facturación</dt><dd>{selectedBillingLabel}</dd></div>
                <div><dt>Collabs de canje</dt><dd>{selectedPlanIsUnlimited ? 'Ilimitados' : `${selectedPlanDetails.max_collabs} activas`}</dd></div>
                <div><dt>Collabs activas</dt><dd>{selectedPlanIsUnlimited ? 'Ilimitadas' : `${selectedPlanDetails.max_collabs}`}</dd></div>
                <div><dt>Creators por collab</dt><dd>{selectedPlanIsUnlimited ? 'Ilimitados' : `Hasta ${selectedPlanDetails.max_creators_per_collab}`}</dd></div>
              </dl>

              <div className="checkout-payment-box">
                {processingPayment && <div className="checkout-loading"><Spinner /><span>Preparando Stripe...</span></div>}
                <Button
                  type="button"
                  variant="dark"
                  className="stripe-button"
                  disabled={processingPayment || hasActiveSubscription}
                  onClick={handleStripeCheckout}
                >
                  <span>{hasActiveSubscription ? 'Suscripción activa' : t('payment.stripe.checkout.buy')}</span>
                  {!hasActiveSubscription && <ArrowRight size={16} aria-hidden="true" />}
                </Button>
              </div>

              <p className="checkout-note">
                <ShieldCheck size={15} aria-hidden="true" />
                <span>{stripeConfig?.test_mode
                  ? 'Estás usando Stripe en modo prueba. No se cobrará dinero real.'
                  : 'FOTH no guarda datos de tarjeta. Stripe procesa y protege tu información de pago.'}</span>
              </p>
            </>
          )}
        </aside>
      </div>

      <div className="payment-support-grid">
        <article className="payment-support-card"><CircleDollarSign size={18} aria-hidden="true" /><div><h2>¿Cómo funcionan los límites?</h2><p>Cada collab activa consume un espacio de tu plan y cada plan define el máximo de creators por collab. Puedes subir de plan cuando necesites más capacidad.</p></div></article>
        <article className="payment-support-card"><Headphones size={18} aria-hidden="true" /><div><h2>¿Necesitas una solución a medida?</h2><p>Corporate incluye acompañamiento especializado para equipos, franquicias y operaciones de alto volumen.</p><a href="mailto:admin@wearefoth.com">Hablar con ventas <ArrowUpRight size={14} aria-hidden="true" /></a></div></article>
      </div>
    </Container>
  );
};

export default PaymentPlans; 
